import { useState, useEffect, useCallback, useRef } from 'react'
import {
  collection,
  doc,
  getDoc,
  getDocs,
  onSnapshot,
  query,
  where,
  orderBy,
  limit,
  Timestamp,
  type QueryConstraint,
} from 'firebase/firestore'
import { db } from '@/lib/firebase/config'
import { useAuth } from '@/contexts/AuthContext'
import { hasPermission } from '@/lib/auth/permissions'
import {
  logFirestoreReadError,
  normalizeFirestoreReadError,
} from '@/lib/firebase/firestoreDiagnostics'
import type { Visit, Incident } from '@/types'
import type { EligibilityCheck } from '@/services/eligibility'

// ============================================
// TYPES
// ============================================

interface UseDocumentResult<T> {
  data: T | null
  isLoading: boolean
  error: Error | null
  refetch: () => void
}

interface UseCollectionResult<T> {
  data: T[]
  isLoading: boolean
  error: Error | null
  refetch: () => void
}

interface UseCollectionOptions {
  orderByField?: string | null
  orderDirection?: 'asc' | 'desc'
  limitCount?: number
  additionalConstraints?: QueryConstraint[]
  enabled?: boolean
  realtime?: boolean
}

// ============================================
// useDocument Hook
// ============================================

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function useDocument<T = any>(
  collectionName: string,
  documentId: string | null | undefined,
  options: { enabled?: boolean; realtime?: boolean } = {}
): UseDocumentResult<T> {
  const { user } = useAuth()
  const { enabled = true, realtime = false } = options
  const [data, setData] = useState<T | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<Error | null>(null)
  const [refetchTrigger, setRefetchTrigger] = useState(0)
  const userRef = useRef(user)
  userRef.current = user

  const refetch = useCallback(() => {
    setRefetchTrigger(prev => prev + 1)
  }, [])

  useEffect(() => {
    if (!documentId || !enabled) {
      setData(null)
      setError(null)
      setIsLoading(false)
      return
    }

    let cancelled = false
    setIsLoading(true)
    setError(null)

    const docRef = doc(db, collectionName, documentId)

    if (realtime) {
      const unsubscribe = onSnapshot(
        docRef,
        (snapshot) => {
          if (cancelled) return
          if (snapshot.exists()) {
            setData({ id: snapshot.id, ...snapshot.data() } as unknown as T)
          } else {
            setData(null)
          }
          setError(null)
          setIsLoading(false)
        },
        (err) => {
          if (cancelled) return
          const normalizedError = normalizeFirestoreReadError(err, {
            collectionName,
            documentPath: `${collectionName}/${documentId}`,
            operation: 'listenDocument',
            user: userRef.current,
          })
          logFirestoreReadError(`Error listening to ${collectionName}/${documentId}:`, normalizedError)
          setError(normalizedError)
          setIsLoading(false)
        }
      )

      return () => {
        cancelled = true
        unsubscribe()
      }
    }

    getDoc(docRef)
      .then((snapshot) => {
        if (cancelled) return
        if (snapshot.exists()) {
          setData({ id: snapshot.id, ...snapshot.data() } as unknown as T)
        } else {
          setData(null)
        }
        setError(null)
        setIsLoading(false)
      })
      .catch((err) => {
        if (cancelled) return
        const normalizedError = normalizeFirestoreReadError(err, {
          collectionName,
          documentPath: `${collectionName}/${documentId}`,
          operation: 'fetchDocument',
          user: userRef.current,
        })
        logFirestoreReadError(`Error fetching ${collectionName}/${documentId}:`, normalizedError)
        setError(normalizedError)
        setIsLoading(false)
      })

    return () => {
      cancelled = true
    }
  }, [collectionName, documentId, enabled, realtime, refetchTrigger])

  return { data, isLoading, error, refetch }
}

// ============================================
// useCollection Hook (with agency isolation)
// ============================================

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function useCollection<T = any>(
  collectionName: string,
  options: UseCollectionOptions = {}
): UseCollectionResult<T> {
  const { user } = useAuth()
  const {
    orderByField = 'createdAt',
    orderDirection = 'desc',
    limitCount = 100,
    additionalConstraints = [],
    enabled = true,
    realtime = false,
  } = options

  const [data, setData] = useState<T[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<Error | null>(null)
  const [refetchTrigger, setRefetchTrigger] = useState(0)
  const userRef = useRef(user)
  userRef.current = user

  // Serialize constraints for dependency tracking
  const constraintsRef = useRef(additionalConstraints)
  constraintsRef.current = additionalConstraints

  const refetch = useCallback(() => {
    setRefetchTrigger(prev => prev + 1)
  }, [])

  useEffect(() => {
    if (!user?.agencyId || !enabled) {
      setData([])
      setError(null)
      setIsLoading(false)
      return
    }

    let cancelled = false
    setIsLoading(true)
    setError(null)

    const constraints: QueryConstraint[] = [
      where('agencyId', '==', user.agencyId),
      ...constraintsRef.current,
    ]

    if (orderByField) {
      constraints.push(orderBy(orderByField, orderDirection))
    }

    constraints.push(limit(limitCount))

    const q = query(collection(db, collectionName), ...constraints)

    if (realtime) {
      // Real-time listener mode
      const unsubscribe = onSnapshot(
        q,
        (snapshot) => {
          if (cancelled) return
          const docs = snapshot.docs.map(doc => ({
            id: doc.id,
            ...doc.data(),
          })) as unknown as T[]
          setData(docs)
          setError(null)
          setIsLoading(false)
        },
        (err) => {
          if (cancelled) return
          const normalizedError = normalizeFirestoreReadError(err, {
            collectionName,
            operation: 'listenCollection',
            user: userRef.current,
            query: {
              agencyScoped: true,
              orderByField,
              orderDirection,
              limitCount,
              additionalConstraintCount: constraintsRef.current.length,
            },
          })
          logFirestoreReadError(`Error listening to ${collectionName}:`, normalizedError)
          setError(normalizedError)
          setIsLoading(false)
        }
      )

      return () => {
        cancelled = true
        unsubscribe()
      }
    } else {
      // One-time fetch mode (HTTP, no WebSocket dependency)
      getDocs(q)
        .then((snapshot) => {
          if (cancelled) return
          const docs = snapshot.docs.map(doc => ({
            id: doc.id,
            ...doc.data(),
          })) as unknown as T[]
          setData(docs)
          setError(null)
          setIsLoading(false)
        })
        .catch((err) => {
          if (cancelled) return
          const normalizedError = normalizeFirestoreReadError(err, {
            collectionName,
            operation: 'fetchCollection',
            user: userRef.current,
            query: {
              agencyScoped: true,
              orderByField,
              orderDirection,
              limitCount,
              additionalConstraintCount: constraintsRef.current.length,
            },
          })
          logFirestoreReadError(`Error fetching ${collectionName}:`, normalizedError)
          setError(normalizedError)
          setIsLoading(false)
        })

      return () => {
        cancelled = true
      }
    }
  }, [collectionName, user?.agencyId, orderByField, orderDirection, limitCount, enabled, realtime, refetchTrigger])

  return { data, isLoading, error, refetch }
}

// ============================================
// useClientsList Hook
// ============================================

export function useClients(options: Omit<UseCollectionOptions, 'additionalConstraints'> & { includeDeleted?: boolean } = {}) {
  const { includeDeleted = false, ...rest } = options

  return useCollection('clients', {
    ...rest,
    additionalConstraints: includeDeleted ? [] : [where('isDeleted', '==', false)],
    orderByField: 'lastName',
    orderDirection: 'asc',
  })
}

// ============================================
// useUsers Hook (caregivers, etc.)
// ============================================

export function useUsers(options: Omit<UseCollectionOptions, 'additionalConstraints'> & { role?: string; includeDeleted?: boolean } = {}) {
  const { role, includeDeleted = false, ...rest } = options
  const constraints: QueryConstraint[] = includeDeleted ? [] : [where('isDeleted', '==', false)]

  if (role) {
    constraints.push(where('role', '==', role))
  }

  return useCollection('users', {
    ...rest,
    additionalConstraints: constraints,
    orderByField: rest.orderByField === undefined ? 'lastName' : rest.orderByField,
    orderDirection: 'asc',
  })
}

export function useMessageContacts() {
  const { user } = useAuth()
  const userRef = useRef(user)
  userRef.current = user

  const [data, setData] = useState<any[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<Error | null>(null)

  useEffect(() => {
    if (!user?.agencyId) {
      setData([])
      setError(null)
      setIsLoading(false)
      return
    }

    setIsLoading(true)
    setError(null)

    let latestUsers: Record<string, unknown>[] = []
    let latestFamilyMembers: Record<string, unknown>[] = []

    const mergeContacts = () => {
      const sorted = [...latestUsers, ...latestFamilyMembers].sort((a, b) => {
        const lastNameCompare = String(a.lastName || '').localeCompare(String(b.lastName || ''))
        if (lastNameCompare !== 0) return lastNameCompare
        return String(a.firstName || '').localeCompare(String(b.firstName || ''))
      })

      setData(sorted)
      setIsLoading(false)
    }

    const usersQuery = query(
      collection(db, 'users'),
      where('agencyId', '==', user.agencyId),
      where('isDeleted', '==', false),
      limit(200)
    )

    const familyMembersQuery = query(
      collection(db, 'familyMembers'),
      where('agencyId', '==', user.agencyId),
      where('isActive', '==', true),
      limit(200)
    )

    const unsubscribeUsers = onSnapshot(
      usersQuery,
      (snapshot) => {
        latestUsers = snapshot.docs
          .map(doc => ({
            id: doc.id,
            ...doc.data(),
          }) as Record<string, unknown>)
          .filter(doc => doc.role !== 'family')

        setError(null)
        mergeContacts()
      },
      (err) => {
        const normalizedError = normalizeFirestoreReadError(err, {
          collectionName: 'users',
          operation: 'listenCollection',
          user: userRef.current,
          query: {
            agencyScoped: true,
            limitCount: 200,
            additionalConstraintCount: 1,
          },
        })
        logFirestoreReadError('[MessageContacts] Users listener error:', normalizedError)
        setError(normalizedError)
        setIsLoading(false)
      }
    )

    const unsubscribeFamily = onSnapshot(
      familyMembersQuery,
      (snapshot) => {
        latestFamilyMembers = snapshot.docs
          .map(doc => ({
            id: doc.id,
            uid: doc.data().uid,
            agencyId: doc.data().agencyId,
            clientId: doc.data().clientId,
            firstName: doc.data().firstName,
            lastName: doc.data().lastName,
            email: doc.data().email,
            phone: doc.data().phone,
            relationship: doc.data().relationship,
            role: 'family',
            status: 'active',
            source: 'familyMember',
          }) as Record<string, unknown>)
          .filter(doc => Boolean(doc.uid))

        setError(null)
        mergeContacts()
      },
      (err) => {
        const normalizedError = normalizeFirestoreReadError(err, {
          collectionName: 'familyMembers',
          operation: 'listenCollection',
          user: userRef.current,
          query: {
            agencyScoped: true,
            limitCount: 200,
            additionalConstraintCount: 1,
          },
        })
        logFirestoreReadError('[MessageContacts] Family members listener error:', normalizedError)
        setError(normalizedError)
        latestFamilyMembers = []
        mergeContacts()
        setIsLoading(false)
      }
    )

    return () => {
      unsubscribeUsers()
      unsubscribeFamily()
    }
  }, [user?.agencyId])

  return { data, isLoading, error }
}

// ============================================
// useVisits Hook
// ============================================

export function useVisits(options: Omit<UseCollectionOptions, 'additionalConstraints'> & {
  clientId?: string
  caregiverId?: string
  status?: string
  date?: Date
  includeDeleted?: boolean
} = {}): UseCollectionResult<Visit> {
  const { user } = useAuth()
  const { clientId, caregiverId, status, date: _date, includeDeleted = false, enabled = true, realtime = false, ...rest } = options

  const [data, setData] = useState<Visit[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<Error | null>(null)
  const [refetchTrigger, setRefetchTrigger] = useState(0)
  const userRef = useRef(user)
  userRef.current = user

  const refetch = useCallback(() => {
    setRefetchTrigger(prev => prev + 1)
  }, [])

  useEffect(() => {
    if (!user?.agencyId || !enabled) {
      setData([])
      setError(null)
      setIsLoading(false)
      return
    }

    let cancelled = false
    setIsLoading(true)
    setError(null)

    const constraints: QueryConstraint[] = [
      where('agencyId', '==', user.agencyId),
    ]

    // Filter soft-deleted records server-side
    if (!includeDeleted) {
      constraints.push(where('isDeleted', '==', false))
    }

    if (clientId) {
      constraints.push(where('clientId', '==', clientId))
    }
    if (caregiverId) {
      constraints.push(where('caregiverId', '==', caregiverId))
    }
    if (status) {
      constraints.push(where('status', '==', status))
    }

    constraints.push(orderBy('scheduledStart', 'asc'))
    constraints.push(limit(rest.limitCount || 500))

    const q = query(collection(db, 'visits'), ...constraints)

    if (realtime) {
      const unsubscribe = onSnapshot(
        q,
        (snapshot) => {
          if (cancelled) return
          const docs = snapshot.docs.map(doc => ({
            id: doc.id,
            ...doc.data(),
          })) as unknown as Visit[]

          setData(docs)
          setError(null)
          setIsLoading(false)
        },
        (err) => {
          if (cancelled) return
          const normalizedError = normalizeFirestoreReadError(err, {
            collectionName: 'visits',
            operation: 'listenCollection',
            user: userRef.current,
            query: {
              agencyScoped: true,
              orderByField: 'scheduledStart',
              orderDirection: 'asc',
              limitCount: rest.limitCount || 500,
              additionalConstraintCount: constraints.length - 3,
            },
          })
          logFirestoreReadError('Error listening to visits:', normalizedError)
          setError(normalizedError)
          setIsLoading(false)
        }
      )

      return () => {
        cancelled = true
        unsubscribe()
      }
    }

    getDocs(q)
      .then((snapshot) => {
        if (cancelled) return
        const docs = snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data(),
        })) as unknown as Visit[]

        setData(docs)
        setError(null)
        setIsLoading(false)
      })
      .catch((err) => {
        if (cancelled) return
        const normalizedError = normalizeFirestoreReadError(err, {
          collectionName: 'visits',
          operation: 'fetchCollection',
          user: userRef.current,
          query: {
            agencyScoped: true,
            orderByField: 'scheduledStart',
            orderDirection: 'asc',
            limitCount: rest.limitCount || 500,
            additionalConstraintCount: constraints.length - 3,
          },
        })
        logFirestoreReadError('Error fetching visits:', normalizedError)
        setError(normalizedError)
        setIsLoading(false)
      })

    return () => {
      cancelled = true
    }
  }, [user?.agencyId, clientId, caregiverId, status, includeDeleted, enabled, realtime, rest.limitCount, refetchTrigger])

  return { data, isLoading, error, refetch }
}

// ============================================
// useIncidents Hook
// ============================================

export function useIncidents(options: Omit<UseCollectionOptions, 'additionalConstraints'> & {
  clientId?: string
  caregiverId?: string
  source?: string
  status?: string
} = {}) {
  const { clientId, caregiverId, source, status, ...rest } = options
  const constraints: QueryConstraint[] = []

  if (clientId) {
    constraints.push(where('clientId', '==', clientId))
  }
  if (caregiverId) {
    constraints.push(where('caregiverId', '==', caregiverId))
  }
  if (source) {
    constraints.push(where('source', '==', source))
  }
  if (status) {
    constraints.push(where('status', '==', status))
  }

  return useCollection<Incident>('incidents', {
    ...rest,
    additionalConstraints: constraints,
    orderByField: 'createdAt',
    orderDirection: 'desc',
  })
}

// ============================================
// useAuthorizations Hook
// ============================================

export function useAuthorizations(options: Omit<UseCollectionOptions, 'additionalConstraints'> & {
  clientId?: string
  status?: string
  includeArchived?: boolean
} = {}) {
  const { user } = useAuth()
  const { clientId, status, includeArchived = false, ...rest } = options
  const canReadAuthorizations = hasPermission(user, 'authorizations.read')
  const constraints: QueryConstraint[] = []

  if (clientId) {
    constraints.push(where('clientId', '==', clientId))
  }
  if (status) {
    constraints.push(where('status', '==', status))
  }

  const result = useCollection('authorizations', {
    ...rest,
    enabled: (rest.enabled ?? true) && canReadAuthorizations,
    additionalConstraints: constraints,
    orderByField: 'endDate',
    orderDirection: 'asc',
  })

  return {
    ...result,
    data: includeArchived
      ? result.data
      : result.data.filter((authorization: { isDeleted?: boolean }) => authorization.isDeleted !== true),
  }
}

// ============================================
// usePlansOfCare Hook
// ============================================

export function usePlansOfCare(options: Omit<UseCollectionOptions, 'additionalConstraints'> & {
  clientId?: string
  status?: string
} = {}) {
  const { clientId, status, ...rest } = options
  const constraints: QueryConstraint[] = []

  if (clientId) {
    constraints.push(where('clientId', '==', clientId))
  }
  if (status) {
    constraints.push(where('status', '==', status))
  }

  return useCollection('plansOfCare', {
    ...rest,
    additionalConstraints: constraints,
    orderByField: 'effectiveDate',
    orderDirection: 'desc',
  })
}

// ============================================
// useNotifications Hook
// ============================================

export function useNotifications(options: { unreadOnly?: boolean } = {}) {
  const { user } = useAuth()
  const { unreadOnly = false } = options

  const [data, setData] = useState<any[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<Error | null>(null)
  const [refetchTrigger, setRefetchTrigger] = useState(0)
  const userRef = useRef(user)
  userRef.current = user

  // refetch triggers a re-subscribe by bumping state (onSnapshot handles real-time updates automatically)
  const refetch = useCallback(() => {
    setRefetchTrigger(prev => prev + 1)
  }, [])

  useEffect(() => {
    if (!user?.uid || !user?.agencyId) {
      setData([])
      setError(null)
      setIsLoading(false)
      return
    }

    setIsLoading(true)
    setError(null)

    // Real-time listener for notifications
    let q
    if (unreadOnly) {
      q = query(
        collection(db, 'notifications'),
        where('agencyId', '==', user.agencyId),
        where('userId', '==', user.uid),
        where('isRead', '==', false),
        orderBy('createdAt', 'desc'),
        limit(50)
      )
    } else {
      q = query(
        collection(db, 'notifications'),
        where('agencyId', '==', user.agencyId),
        where('userId', '==', user.uid),
        orderBy('createdAt', 'desc'),
        limit(50)
      )
    }

    const unsubscribe = onSnapshot(
      q,
      (snapshot) => {
        const docs = snapshot.docs.map(d => ({
          id: d.id,
          ...d.data(),
        }))
        setData(docs)
        setError(null)
        setIsLoading(false)
      },
      (err) => {
        const normalizedError = normalizeFirestoreReadError(err, {
          collectionName: 'notifications',
          operation: 'listenCollection',
          user: userRef.current,
          query: {
            agencyScoped: true,
            orderByField: 'createdAt',
            orderDirection: 'desc',
            limitCount: 50,
            additionalConstraintCount: unreadOnly ? 2 : 1,
          },
        })
        logFirestoreReadError('[Notifications] Listener error:', normalizedError)
        setError(normalizedError)
        setIsLoading(false)
      }
    )

    return unsubscribe
  }, [user?.uid, user?.agencyId, unreadOnly, refetchTrigger])

  return { data, isLoading, error, refetch }
}

// ============================================
// useMessageThreads Hook
// ============================================

export function useMessageThreads() {
  const { user } = useAuth()

  return useCollection('messageThreads', {
    additionalConstraints: user?.uid
      ? [where('participantIds', 'array-contains', user.uid)]
      : [],
    orderByField: 'lastMessageAt',
    orderDirection: 'desc',
    enabled: !!user?.uid,
    realtime: true,
  })
}

// ============================================
// useMessages Hook
// ============================================

export function useMessages(threadId: string | null | undefined) {
  const { user } = useAuth()
  const [data, setData] = useState<any[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<Error | null>(null)
  const [refetchTrigger, setRefetchTrigger] = useState(0)
  const mountedRef = useRef(true)
  const userRef = useRef(user)
  userRef.current = user

  const refetch = useCallback(() => {
    setRefetchTrigger(prev => prev + 1)
  }, [])

  // Track mounted state
  useEffect(() => {
    mountedRef.current = true
    return () => {
      mountedRef.current = false
    }
  }, [])

  useEffect(() => {
    if (!threadId) {
      setData([])
      setError(null)
      setIsLoading(false)
      return
    }

    let cancelled = false
    setIsLoading(true)
    setError(null)

    // Simple query: just filter by threadId and order by createdAt
    // No agencyId filter needed since threadId is already unique
    const q = query(
      collection(db, 'messages'),
      where('threadId', '==', threadId),
      orderBy('createdAt', 'asc'),
      limit(100)
    )

    const unsubscribe = onSnapshot(
      q,
      (snapshot) => {
        if (cancelled || !mountedRef.current) return
        const docs = snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data(),
        }))
        setData(docs)
        setError(null)
        setIsLoading(false)
      },
      (err) => {
        if (cancelled || !mountedRef.current) return
        const normalizedError = normalizeFirestoreReadError(err, {
          collectionName: 'messages',
          operation: 'listenCollection',
          user: userRef.current,
          query: {
            agencyScoped: false,
            orderByField: 'createdAt',
            orderDirection: 'asc',
            limitCount: 100,
            additionalConstraintCount: 1,
          },
        })
        logFirestoreReadError('Error fetching messages:', normalizedError)
        setError(normalizedError)
        setIsLoading(false)
      }
    )

    return () => {
      cancelled = true
      unsubscribe()
    }
  }, [threadId, refetchTrigger])

  return { data, isLoading, error, refetch }
}

// ============================================
// useAgencySettings Hook
// ============================================

export function useAgencySettings() {
  const { user } = useAuth()

  return useDocument('agencies/' + user?.agencyId + '/settings', 'config', {
    enabled: !!user?.agencyId,
  })
}

// ============================================
// useEligibilityChecks Hook
// ============================================

export function useEligibilityChecks(params: {
  agencyId: string
  status?: 'active' | 'inactive' | 'unknown'
  payerId?: string
  clientId?: string
  startDate?: Date
  endDate?: Date
  limit?: number
  enabled?: boolean
}): UseCollectionResult<EligibilityCheck> {
  const { user } = useAuth()
  const {
    agencyId,
    status,
    payerId,
    clientId,
    startDate,
    endDate,
    limit: limitCount = 100,
    enabled = true,
  } = params

  const [data, setData] = useState<EligibilityCheck[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<Error | null>(null)
  const [refetchTrigger, setRefetchTrigger] = useState(0)
  const userRef = useRef(user)
  userRef.current = user

  const refetch = useCallback(() => {
    setRefetchTrigger(prev => prev + 1)
  }, [])

  const startMs = startDate?.getTime()
  const endMs = endDate?.getTime()

  useEffect(() => {
    if (!agencyId || !enabled) {
      setData([])
      setError(null)
      setIsLoading(false)
      return
    }

    let cancelled = false
    setIsLoading(true)
    setError(null)

    const constraints: QueryConstraint[] = [
      where('agencyId', '==', agencyId),
    ]

    if (status) {
      constraints.push(where('summary.status', '==', status))
    }

    if (payerId) {
      constraints.push(where('payerId', '==', payerId))
    }

    if (clientId) {
      constraints.push(where('clientId', '==', clientId))
    }

    if (startMs !== undefined) {
      constraints.push(where('checkedAt', '>=', Timestamp.fromDate(new Date(startMs))))
    }

    if (endMs !== undefined) {
      constraints.push(where('checkedAt', '<=', Timestamp.fromDate(new Date(endMs))))
    }

    constraints.push(orderBy('checkedAt', 'desc'))
    constraints.push(limit(limitCount))

    const q = query(collection(db, 'eligibilityChecks'), ...constraints)

    getDocs(q)
      .then((snapshot) => {
        if (cancelled) return
        const docs = snapshot.docs.map(d => ({
          id: d.id,
          ...d.data(),
        })) as unknown as EligibilityCheck[]

        setData(docs)
        setError(null)
        setIsLoading(false)
      })
      .catch((err) => {
        if (cancelled) return
        const normalizedError = normalizeFirestoreReadError(err, {
          collectionName: 'eligibilityChecks',
          operation: 'fetchCollection',
          user: userRef.current,
          query: {
            agencyScoped: true,
            orderByField: 'checkedAt',
            orderDirection: 'desc',
            limitCount,
            additionalConstraintCount: constraints.length - 3,
          },
        })
        logFirestoreReadError('Error fetching eligibility checks:', normalizedError)
        setError(normalizedError)
        setIsLoading(false)
      })

    return () => {
      cancelled = true
    }
  }, [agencyId, status, payerId, clientId, startMs, endMs, limitCount, enabled, refetchTrigger])

  return { data, isLoading, error, refetch }
}
